import { gql, useApolloClient } from "@apollo/client";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { assertNever, getDataOrNull, getFileName, notNull } from "@msys/common";
import {
  PopoverOpenButton,
  PopoverOpenButtonRef,
  useDownloadFile,
} from "@msys/ui";
import {
  Button,
  CircularProgress,
  Icon,
  Stack,
  Typography,
} from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium.js";
import { useTranslate } from "@tolgee/react";
import { isEmpty, uniqueId } from "lodash-es";
import moment from "moment";
import { useSnackbar } from "notistack";
import React from "react";
import { ButtonCreate } from "../../../commons/button/Button.js";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import { useOpenFile } from "../../../commons/hooks/useOpenFile.js";
import {
  BillOfMaterialsExportForm,
  FormValues,
} from "../../bill-of-materials/BillOfMaterialsExportForm.js";
import { columns } from "../../bill-of-materials/useColumnLabel.js";
import { OrganisationSettingBillOfMaterialsExportSettingsFragment } from "../../organisations/boxes/OrganisationBillOfMaterialsSettingsBox.generated.js";
import {
  GenerateBillOfMaterialsPdfDocument,
  GenerateBillOfMaterialsPdfQuery,
  GenerateBillOfMaterialsPdfQueryVariables,
} from "./BillOfMaterialsExportButton.generated.js";

export function BillOfMaterialsExportButton({
  // apiRef,
  withGridApi,
  projectId,
  projectTitle,
  filters,
  compact,
  billOfMaterialsExportSettings,
}: {
  withGridApi: (
    fn: (gridApi: GridApiPremium) => Promise<void>
  ) => Promise<void>;
  // apiRef: React.MutableRefObject<GridApiPremium | undefined>;
  projectId: string;
  projectTitle: string;
  filters: Omit<GenerateBillOfMaterialsPdfQueryVariables, "projectId">;
  compact?: true;
  billOfMaterialsExportSettings: OrganisationSettingBillOfMaterialsExportSettingsFragment;
}) {
  const { t } = useTranslate(["PurchaseOrders", "Global"]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const { downloadFileAsBlob } = useDownloadFile();

  const popoverOpenButtonRef = React.useRef<PopoverOpenButtonRef>();

  const { enqueueSnackbar } = useSnackbar();
  const { openPdf } = useOpenFile();

  const client = useApolloClient();

  const handleCsvDownload = async (values: FormValues) => {
    if (loading) return;
    setLoading(true);
    try {
      /* prettier-ignore */
      const filShortName = `BillOfMaterials_${getFileName(projectTitle).substring(0, 64)}`;
      const fileName = `${filShortName}_${moment().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}`;
      await withGridApi(async apiRef => {
        const csv = apiRef.getDataAsCsv({
          fields: columns
            .filter(c => values.csvFields.includes(c))
            .map(c => (c === "productTitle" ? "__tree_data_group__" : c)),
          fileName: fileName,
          includeHeaders: true,
          utf8WithBom: true,
          getRowsToExport: ({ apiRef }) =>
            apiRef.current
              ?.getSortedRows()
              ?.map((r, index) => {
                const rowId = apiRef.current?.getRowIdFromRowIndex(index);
                // normal items
                if (r.itemType) {
                  if (r.itemType === "quote_item") return null;
                  return rowId;
                }
                // auto-generated items
                if (
                  rowId === "auto-generated-row-null/quote_item" ||
                  rowId === "auto-generated-row-null/product" ||
                  rowId === "auto-generated-row-null/custom_product"
                )
                  return null;
                return rowId;
              })
              .filter(notNull) ?? [],
        });
        if (csv) {
          // trying to download it
          const blob = new Blob([csv], { type: "text/csv" });
          await downloadFileAsBlob(blob, `${filShortName}.csv`);
        }
      });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleXlsxDownload = async (values: FormValues) => {
    if (loading) return;
    setLoading(true);
    try {
      /* prettier-ignore */
      const filShortName = `BillOfMaterials_${getFileName(projectTitle).substring(0, 64)}`;
      const fileName = `${filShortName}_${moment().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}`;
      await withGridApi(async apiRef => {
        const workbook = await apiRef.getDataAsExcel({
          fields: columns
            .filter(c => values.csvFields.includes(c))
            .map(c => (c === "productTitle" ? "__tree_data_group__" : c)),
          fileName: fileName,
          includeHeaders: true,
          getRowsToExport: ({ apiRef }) =>
            apiRef.current
              ?.getSortedRows()
              ?.map((r, index) => {
                const rowId = apiRef.current?.getRowIdFromRowIndex(index);
                // normal items
                if (r.itemType) {
                  if (r.itemType === "quote_item") return null;
                  return rowId;
                }
                // auto-generated items
                if (
                  rowId === "auto-generated-row-null/quote_item" ||
                  rowId === "auto-generated-row-null/product" ||
                  rowId === "auto-generated-row-null/custom_product"
                )
                  return null;
                return rowId;
              })
              .filter(notNull) ?? [],
        });
        if (workbook) {
          // trying to download it
          const buffer = await workbook.xlsx.writeBuffer();
          const blob = new Blob([buffer], {
            // source: https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          await downloadFileAsBlob(blob, `${filShortName}.xlsx`);
        }
      });
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handlePdfDownload = async (values: FormValues) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await client.query<
        GenerateBillOfMaterialsPdfQuery,
        GenerateBillOfMaterialsPdfQueryVariables
      >({
        query: GenerateBillOfMaterialsPdfDocument,
        variables: {
          projectId,
          ...filters,
          sections: values.sections,
          fields: values.pdfFields,
        },
      });

      const url = getDataOrNull(data?.billOfMaterialsGeneratePdf)?.url;
      if (!url) throw new Error("Failed to generate PDF");
      await openPdf(
        url,
        `BillOfMaterials_${getFileName(projectTitle).substring(0, 64)}`
      );
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    switch (values.dataFormat) {
      case "pdf": {
        await handlePdfDownload(values);
        break;
      }
      case "csv": {
        await handleCsvDownload(values);
        break;
      }
      case "xlsx": {
        await handleXlsxDownload(values);
        break;
      }
      default: {
        assertNever(values.dataFormat);
      }
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <PopoverOpenButton
      popoverRef={popoverOpenButtonRef}
      content={
        <Stack p={1} direction="column" spacing={1} width="320px">
          <Typography variant="h3">
            {t("Material list setup", { ns: "PurchaseOrders" })}
          </Typography>
          <BillOfMaterialsExportForm
            autoSave={false}
            formId={formId}
            initialValues={billOfMaterialsExportSettings}
            onSubmit={handleSubmit}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={() => popoverOpenButtonRef.current?.handleClose()}
              size="small"
              variant="text"
              color="primary"
            >
              {t("Cancel", { ns: "Global" })}
            </Button>
            <Button
              form={formId}
              type="submit"
              size="small"
              variant="contained"
              color="primary"
              loading={loading}
              startIcon={
                <Icon>
                  <FontAwesomeIcon icon={faFileExport} />
                </Icon>
              }
            >
              {t("Export", { ns: "Global" })}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <ButtonCreate
        title={t("Export material list", { ns: "PurchaseOrders" })}
        Icon={
          loading ? (
            <CircularProgress size="1rem" color={"primary"} />
          ) : (
            <Icon>
              <FontAwesomeIcon icon={faFileExport} />
            </Icon>
          )
        }
        variant="text"
        compact={compact}
      />
    </PopoverOpenButton>
  );
}

export function useForceGridApi(
  apiRef: React.MutableRefObject<GridApiPremium | undefined>
) {
  const [forcedCollectionView, setForcedCollectionView] =
    React.useState<CollectionView | null>(null);
  const resolveRef = React.useRef<((gridApi: GridApiPremium) => void) | null>(
    null
  );
  const onGridApiRef = React.useCallback((gridApi: GridApiPremium | null) => {
    if (gridApi && !isEmpty(gridApi) && resolveRef.current) {
      resolveRef.current(gridApi);
    }
  }, []);
  const forceGetGridApi = (): Promise<GridApiPremium> =>
    new Promise(resolve => {
      if (apiRef.current && !isEmpty(apiRef.current)) {
        resolve(apiRef.current);
      } else {
        resolveRef.current = resolve;
        setForcedCollectionView("table");
      }
    });
  const withGridApi = async (
    fn: (gridApi: GridApiPremium) => Promise<void>
  ) => {
    try {
      const gridApi = await forceGetGridApi();
      await fn(gridApi);
    } finally {
      setForcedCollectionView(null);
    }
  };
  return { forcedCollectionView, onGridApiRef, withGridApi };
}
